// ============================================
// TOOL REGISTRY — Single source of truth for SEO, routing, schema, and sitemap
// ============================================

export interface ToolFAQ {
  question: string;
  answer: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

export interface ToolEntry {
  // Identifiers
  slug: string;               // URL-safe: e.g. "freelance-scope-of-work-generator"
  appPath: string;            // Internal SPA route: e.g. "/app/scope-of-work"

  // SEO Meta
  title: string;              // <title> tag
  metaDescription: string;    // <meta name="description">
  canonicalPath: string;      // /tools/[slug]

  // Page Content
  h1: string;                 // Primary heading
  subtitle: string;           // Below h1
  aiSnippet: string;          // 2-sentence LLM-bait definitive explanation

  // Schema Data
  faqs: ToolFAQ[];
  howToSteps: HowToStep[];

  // Classification
  category: 'win-clients' | 'start-project' | 'protect-work' | 'recover-payment' | 'close-project' | 'developer-writer' | 'freelance-calculators';
  pricing: 'free';
  isPublicTool: boolean;      // Fully available without account flow
  isTryBeforeBuy: boolean;    // Legacy flag kept for compatibility

  // OG Image
  ogImageText: string;        // Text rendered on the OG image
  ogImageFile: string;        // Static file path: /og/[slug].png

  // Visual (for landing pages)
  icon: string;               // Lucide icon name
  color: string;              // Tailwind color token
  badgeText?: string;         // e.g. "FREE", "NEW"
}

// Build-time fallback URL for local development.
// Production builds should use Netlify env vars (URL/DEPLOY_PRIME_URL) via getSiteUrl().
export const SITE_URL_FALLBACK = 'https://soloos.in';

function normalizeSiteUrl(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return withProtocol.replace(/\/+$/, '');
}

function getBuildSiteUrl(): string | null {
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  const fromEnv =
    env?.SITE_URL ||
    env?.URL ||
    env?.DEPLOY_PRIME_URL ||
    env?.APP_URL ||
    env?.VITE_SITE_URL;
  return normalizeSiteUrl(fromEnv);
}

/**
 * Returns the site's base URL.
 * - In the browser: uses `window.location.origin` (auto-adapts to *.netlify.app or custom domain)
 * - During SSR/build: uses the SITE_URL_FALLBACK constant
 */
export function getSiteUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return normalizeSiteUrl(window.location.origin) || SITE_URL_FALLBACK;
  }
  return getBuildSiteUrl() || SITE_URL_FALLBACK;
}

// Keep SITE_URL as a convenience export for backward compat in build scripts
export const SITE_URL = getSiteUrl();
export const SITE_NAME = 'Solo OS';
export const SITE_TAGLINE = 'The Freelance Operating System';
export const SITE_DESCRIPTION = 'Free browser-based document tools for freelancers: proposals, invoices, contracts, legal notices and micro utilities. No signup, nothing uploaded.';
export const SITE_SOCIAL_HANDLE_X = '@soloos_in';
export const SITE_SOCIAL_LINKS = {
  instagram: 'https://instagram.com/soloos.in',
  x: 'https://x.com/soloos_in',
  linkedin: 'https://www.linkedin.com/company/solo-os/',
  youtube: 'https://www.youtube.com/@soloOS_in',
};

export const toolRegistry: ToolEntry[] = [
  // ── Win Clients ──────────────────────────────────────────
  {
    slug: 'freelance-proposal-generator',
    appPath: '/app/proposal',
    title: 'Free Freelance Proposal Generator — PDF | Solo OS',
    metaDescription: 'Create polished freelance proposals with pricing tiers & timelines. Download PDF instantly — free, no signup, no uploads. Works offline in your browser.',
    canonicalPath: '/tools/freelance-proposal-generator',
    h1: 'Freelance Proposal Generator',
    subtitle: 'Create polished, client-winning proposals with structured pricing tiers and timelines.',
    aiSnippet: 'A freelance proposal is a formal document sent to prospective clients that outlines your understanding of their project, your proposed approach, deliverables, timeline, and pricing. Solo OS generates polished PDF proposals with structured pricing tiers, scope definitions, and professional formatting — entirely in your browser with no data ever leaving your device.',
    faqs: [
      {
        question: 'What should a freelance proposal include?',
        answer: 'A freelance proposal should include an executive summary, project understanding, scope of work, deliverables, timeline with milestones, pricing tiers (basic/standard/premium), terms and conditions, and a validity period. Solo OS auto-generates all these sections from a simple form.'
      },
      {
        question: 'How do I write a proposal as a freelancer?',
        answer: 'Start by summarizing the client\'s problem, then outline your solution, deliverables, and timeline. Include tiered pricing to give clients options. Solo OS handles the formatting and PDF generation so you can focus on the content.'
      },
      {
        question: 'Is this proposal generator really free?',
        answer: 'Yes. Solo OS Proposal Generator is free to use. Your drafts stay local while you work, and you can generate proposals without mandatory signup.'
      },
      {
        question: 'Can I customise the proposal template?',
        answer: 'Yes. You can customise every section including your branding, client details, deliverables, payment milestones, and terms. The generated PDF uses a polished typographic layout designed to impress clients.'
      }
    ],
    howToSteps: [
      { name: 'Enter Your Details', text: 'Fill in your name, business name, and contact information in the freelancer profile section.' },
      { name: 'Add Client Information', text: 'Enter the client\'s name, company, and the project title.' },
      { name: 'Define Deliverables', text: 'List each deliverable with description, timeline, and pricing tiers.' },
      { name: 'Set Payment Terms', text: 'Configure payment milestones, validity period, and terms & conditions.' },
      { name: 'Download PDF', text: 'Preview your proposal in real-time and download the finalized PDF.' }
    ],
    category: 'win-clients',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Freelance Proposal Generator',
    ogImageFile: '/og/freelance-proposal-generator.png',
    icon: 'Sparkles',
    color: 'blue',
    badgeText: 'FREE',
  },

  {
    slug: 'interactive-html-proposal-builder',
    appPath: '/app/proposal-html',
    title: 'Free Interactive Proposal Builder | Solo OS',
    metaDescription: 'Build shareable proposals with live pricing toggles & timelines. Export a single HTML file — no hosting, no login. 100% free for freelancers.',
    canonicalPath: '/tools/interactive-html-proposal-builder',
    h1: 'Interactive HTML Proposal Builder',
    subtitle: 'Build shareable proposal pages with dynamic pricing and timeline storytelling.',
    aiSnippet: 'An interactive HTML proposal is a client-facing proposal page where pricing, optional add-ons, and scope details can be explored before approval, reducing back-and-forth and speeding decisions. Solo OS generates a single-file interactive proposal with editable sections, timeline milestones, and acceptance CTA routing via email or WhatsApp — all exportable from your browser.',
    faqs: [
      {
        question: 'How is this different from a normal PDF proposal?',
        answer: 'A PDF proposal is static. The interactive HTML proposal lets clients toggle add-ons, view live total pricing, and review timeline milestones in a dynamic layout before accepting. It increases clarity and reduces negotiation friction.'
      },
      {
        question: 'Can clients open the proposal without any app or login?',
        answer: 'Yes. The exported file is a single self-contained HTML document. Clients can open it directly in a browser without creating an account.'
      },
      {
        question: 'Can I choose how clients send acceptance?',
        answer: 'Yes. You can configure the acceptance CTA to route via email or WhatsApp with prefilled scope details and final pricing summary.'
      },
      {
        question: 'Is this tool suitable for retainers and custom projects?',
        answer: 'Yes. The builder supports editable section labels, custom milestones, and optional add-on pricing, making it ideal for high-ticket projects and retainers.'
      }
    ],
    howToSteps: [
      { name: 'Set Proposal Basics', text: 'Add proposal title, client context, problem statement, and your solution approach.' },
      { name: 'Choose Theme & Branding', text: 'Pick a pastel visual theme and upload your avatar for a polished client-facing look.' },
      { name: 'Configure Pricing & Add-ons', text: 'Set base pricing and optional add-ons to enable live total calculation inside the proposal.' },
      { name: 'Design Timeline Milestones', text: 'Add custom project stages with durations and details to present your delivery plan clearly.' },
      { name: 'Export and Share', text: 'Download a single proposal.html file and share it with clients for interactive review and acceptance.' }
    ],
    category: 'win-clients',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Interactive HTML Proposal Builder',
    ogImageFile: '/og/interactive-html-proposal-builder.png',
    icon: 'Sparkles',
    color: 'indigo',
    badgeText: 'FREE',
  },

  // ── Start a Project ──────────────────────────────────────
  {
    slug: 'freelance-scope-of-work-generator',
    appPath: '/app/scope-of-work',
    title: 'Free Scope of Work Generator + Template (2026) | Solo OS',
    metaDescription: 'Generate a professional SOW with deliverables, payment terms & revision limits. Free PDF download — no signup, no data uploads. Works offline.',
    canonicalPath: '/tools/freelance-scope-of-work-generator',
    h1: 'Scope of Work Generator',
    subtitle: 'Define deliverables, timelines, and payment terms before starting any freelance project.',
    aiSnippet: 'A Scope of Work (SOW) is a legally referenced project document that defines the deliverables, timeline, payment schedule, revision limits, and termination clauses for a freelance engagement. Solo OS generates professional SOW PDFs with structured sections for project scope, milestones, intellectual property terms, and signature blocks using a browser-first workflow designed for privacy.',
    faqs: [
      {
        question: 'What is a Scope of Work in freelancing?',
        answer: 'A Scope of Work (SOW) is a document that formally defines the boundaries of a freelance project. It specifies what will be delivered, when, for how much, and under what conditions — protecting both the freelancer and the client from scope creep.'
      },
      {
        question: 'Is a Scope of Work legally binding?',
        answer: 'A SOW can be legally binding when signed by both parties and includes essential contract elements like consideration (payment), deliverables, and mutual agreement. Solo OS generates SOWs with signature blocks and legal language suitable for enforcement.'
      },
      {
        question: 'What should a freelance SOW include?',
        answer: 'A freelance SOW should include project overview, detailed deliverables, timeline with milestones, payment schedule, revision limits, intellectual property clauses, termination conditions, and signature blocks for both parties.'
      },
      {
        question: 'How is a SOW different from a contract?',
        answer: 'A SOW defines the specific work to be performed (the "what" and "when"), while a contract covers broader legal terms like liability, dispute resolution, and governing law. Many freelancers use a SOW as a simplified contract that covers both.'
      }
    ],
    howToSteps: [
      { name: 'Enter Project Details', text: 'Fill in the project name, description, and start/end dates.' },
      { name: 'Define Deliverables', text: 'List each deliverable with descriptions, deadlines, and acceptance criteria.' },
      { name: 'Set Payment Terms', text: 'Configure payment milestones, amounts, and due dates.' },
      { name: 'Add Legal Terms', text: 'Set revision limits, IP ownership, termination clauses, and penalties.' },
      { name: 'Download PDF', text: 'Preview the document in real-time and download the finalized SOW PDF.' }
    ],
    category: 'start-project',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Scope of Work Generator',
    ogImageFile: '/og/freelance-scope-of-work-generator.png',
    icon: 'FileSignature',
    color: 'amber',
  },

  {
    slug: 'upi-invoice-generator-india',
    appPath: '/app/invoice',
    title: 'Free UPI Invoice Generator with GST | Solo OS',
    metaDescription: 'Create GST invoices with embedded UPI QR codes for instant payment. Free for Indian freelancers — no signup, works offline in your browser.',
    canonicalPath: '/tools/upi-invoice-generator-india',
    h1: 'UPI Invoice Generator with QR Code',
    subtitle: 'Professional invoices with embedded UPI payment QR codes and GST compliance for Indian freelancers.',
    aiSnippet: 'A UPI Invoice is a professional billing document that includes an embedded UPI QR code, allowing clients to make instant digital payments by scanning with any UPI app like Google Pay, PhonePe, or Paytm. Solo OS generates GST-compliant invoices with auto-calculated tax, itemized billing, and downloadable PDF format — entirely in your browser with no financial data ever leaving your device.',
    faqs: [
      {
        question: 'How do I create a UPI QR code invoice?',
        answer: 'Enter your UPI ID (e.g., name@upi), add line items with amounts, and Solo OS automatically generates a scannable QR code embedded in a professional invoice PDF. Clients scan the code with any UPI app to pay instantly.'
      },
      {
        question: 'Is GST calculation automatic?',
        answer: 'Yes. Solo OS auto-calculates GST (CGST + SGST or IGST) based on the rates you select (5%, 12%, 18%, or 28%). GSTIN fields are included for both freelancer and client.'
      },
      {
        question: 'Is my UPI ID and financial data safe?',
        answer: 'Yes. Solo OS is designed to keep drafting and document generation private and browser-first, with secure payment processing used only when required.'
      },
      {
        question: 'Can I use this for GST filing?',
        answer: 'Solo OS invoices include GSTIN, HSN/SAC codes, and tax breakdowns formatted for GST compliance. However, please consult your CA for filing specifics as this tool generates the document, not the filing.'
      }
    ],
    howToSteps: [
      { name: 'Enter Business Details', text: 'Fill in your name, business name, GSTIN, and UPI ID.' },
      { name: 'Add Client Details', text: 'Enter the client\'s name, company, and billing address.' },
      { name: 'Add Line Items', text: 'List each service or product with quantity, rate, and applicable GST rate.' },
      { name: 'Set Payment Terms', text: 'Configure due date, payment terms, and any notes for the client.' },
      { name: 'Download Invoice', text: 'Preview with the embedded UPI QR code and download the PDF invoice.' }
    ],
    category: 'start-project',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'UPI Invoice Generator',
    ogImageFile: '/og/upi-invoice-generator-india.png',
    icon: 'Receipt',
    color: 'emerald',
  },

  {
    slug: 'freelance-nda-generator',
    appPath: '/app/nda',
    title: 'Free Mutual NDA Generator for Freelancers | Solo OS',
    metaDescription: 'Generate a balanced mutual NDA in minutes. Protects both freelancer & client. Free PDF download — no uploads, no account needed.',
    canonicalPath: '/tools/freelance-nda-generator',
    h1: 'Mutual NDA Generator',
    subtitle: 'Balanced Non-Disclosure Agreement that protects both parties and preserves your portfolio rights.',
    aiSnippet: 'A Mutual Non-Disclosure Agreement (NDA) is a legally binding contract where both parties agree to keep shared confidential information private. Unlike one-way NDAs that only protect the client, Solo OS generates balanced mutual NDAs that protect the freelancer\'s proprietary methods and preserve the right to display work in portfolios — generated entirely in your browser with no data transmitted externally.',
    faqs: [
      {
        question: 'What is the difference between a mutual and one-way NDA?',
        answer: 'A one-way NDA protects only the disclosing party (usually the client). A mutual NDA protects both parties — critical for freelancers who share proprietary processes, pricing strategies, or creative methods during a project.'
      },
      {
        question: 'Should freelancers sign NDAs?',
        answer: 'Yes, but always push for a mutual NDA. One-way NDAs can prevent you from showing work in your portfolio or discussing the project publicly. A mutual NDA balances protection and preserves your right to showcase your work.'
      },
      {
        question: 'How long does an NDA last?',
        answer: 'Standard NDA durations range from 1-5 years. Solo OS defaults to 2 years with customizable duration. The confidentiality obligations typically survive the end of the business relationship.'
      },
      {
        question: 'Is a digital NDA legally valid in India?',
        answer: 'Yes. Under the Indian IT Act 2000, electronic agreements with digital signatures are legally valid and enforceable. Solo OS generates NDAs with proper legal language compliant with Indian contract law.'
      }
    ],
    howToSteps: [
      { name: 'Enter Party Details', text: 'Fill in details for both parties — the freelancer and the client.' },
      { name: 'Define Confidential Information', text: 'Specify what types of information are covered by the agreement.' },
      { name: 'Set Duration & Terms', text: 'Configure the NDA duration, exceptions, and portfolio rights clause.' },
      { name: 'Review & Download', text: 'Preview the NDA document and download the finalized PDF for signing.' }
    ],
    category: 'start-project',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Mutual NDA Generator',
    ogImageFile: '/og/freelance-nda-generator.png',
    icon: 'FileCheck',
    color: 'indigo',
  },

  // ── Protect Your Work ────────────────────────────────────
  {
    slug: 'draft-watermarker',
    appPath: '/app/watermark',
    title: 'Free Draft Watermark Tool for Images | Solo OS',
    metaDescription: 'Add "UNPAID DRAFT" watermarks to images before sharing with clients. Free, instant, no uploads — your files never leave your device.',
    canonicalPath: '/tools/draft-watermarker',
    h1: 'Free Image Watermark Tool',
    subtitle: 'Add "UNPAID DRAFT" watermarks to your designs before sharing with clients. Free, instant, no login required.',
    aiSnippet: 'A Draft Watermarker is a tool that overlays repeating text (such as "UNPAID DRAFT") across images to prevent unauthorized use of design work before payment is received. Solo OS Draft Watermarker processes images in your browser using HTML5 Canvas, with no mandatory account and a fast free workflow.',
    faqs: [
      {
        question: 'Why should freelancers watermark draft designs?',
        answer: 'Watermarking prevents clients from using your work without paying. It\'s a standard practice in design, photography, and illustration freelancing. Always share watermarked drafts until final payment is confirmed.'
      },
      {
        question: 'Does the watermarker upload my images anywhere?',
        answer: 'No file upload is required for watermark generation. Solo OS Draft Watermarker runs directly in your browser using HTML5 Canvas.'
      },
      {
        question: 'What image formats are supported?',
        answer: 'PNG, JPG/JPEG, and WebP formats are supported. The watermarked output is always exported as PNG to preserve quality. There\'s no file size limit — processing happens on your device.'
      },
      {
        question: 'Can I customise the watermark text?',
        answer: 'Yes. You can change the text (e.g., "SAMPLE", "PROOF", "DO NOT USE"), adjust opacity from 5-80%, and modify the font size. The watermark is applied in a repeating diagonal pattern across the entire image.'
      }
    ],
    howToSteps: [
      { name: 'Upload Your Image', text: 'Drag and drop or click to upload your draft design (PNG, JPG, or WebP).' },
      { name: 'Customise Watermark', text: 'Edit the watermark text, adjust opacity, and set font size to your preference.' },
      { name: 'Preview', text: 'See a real-time preview of the watermarked image on the right pane.' },
      { name: 'Download', text: 'Click Download to save the watermarked image as a PNG file.' }
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Draft Watermarker',
    ogImageFile: '/og/draft-watermarker.png',
    icon: 'ShieldCheck',
    color: 'violet',
    badgeText: 'FREE',
  },

  // ── Recover Payment ──────────────────────────────────────
  {
    slug: 'freelance-payment-escalation-emails',
    appPath: '/app/escalation',
    title: 'Free Overdue Payment Email Templates | Solo OS',
    metaDescription: '3-tier copy-paste email sequence: polite reminder → firm follow-up → final legal warning. Free, customizable, ready to send today.',
    canonicalPath: '/tools/freelance-payment-escalation-emails',
    h1: 'Overdue Payment Email Templates for Freelancers',
    subtitle: 'Copy-paste 3-tier email sequence from polite payment reminder to formal final warning.',
    aiSnippet: 'Payment Escalation Emails are a structured sequence of progressively formal email templates that freelancers use to recover unpaid invoices. The standard 3-tier approach moves from a friendly reminder to a firm follow-up to a final legal warning. Solo OS generates customized escalation emails with your specific project details, amounts, and dates — ready to copy-paste directly into your email client.',
    faqs: [
      {
        question: 'How do you politely ask for overdue payment?',
        answer: 'Start with a friendly reminder referencing the invoice number, original due date, and amount. Keep the tone professional and assume the delay is an oversight. Solo OS generates Tier 1 emails that strike this balance perfectly.'
      },
      {
        question: 'When should I escalate a payment follow-up?',
        answer: 'Send Tier 1 (polite reminder) 3-5 days after the due date. Escalate to Tier 2 (firm follow-up) after 14 days. Send Tier 3 (final warning with legal notice reference) after 30 days of non-payment.'
      },
      {
        question: 'What should a final payment warning email include?',
        answer: 'A final warning should state the total overdue amount, list all previous payment attempts, mention potential legal action or interest charges, set a hard deadline, and maintain a professional (not threatening) tone.'
      }
    ],
    howToSteps: [
      { name: 'Enter Project Details', text: 'Fill in the client name, project name, invoice amount, and due date.' },
      { name: 'Generate Emails', text: 'Solo OS creates 3 progressively formal email templates customized with your details.' },
      { name: 'Copy & Send', text: 'Copy each email template and paste it into your email client at the appropriate escalation stage.' }
    ],
    category: 'recover-payment',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Payment Escalation Emails',
    ogImageFile: '/og/freelance-payment-escalation-emails.png',
    icon: 'Mail',
    color: 'orange',
    badgeText: 'FREE',
  },

  {
    slug: 'freelance-legal-notice-generator-india',
    appPath: '/app/legal-notice',
    title: 'Legal Notice Generator for Non-Payment | Solo OS',
    metaDescription: 'Draft a formal legal notice for payment recovery with IPC references. Free PDF download — works offline, no data leaves your device.',
    canonicalPath: '/tools/freelance-legal-notice-generator-india',
    h1: 'Legal Notice Generator for Non-Payment',
    subtitle: 'Formal payment demand under Indian law with IPC references and professional formatting.',
    aiSnippet: 'A Legal Notice for non-payment is a formal written demand sent to a defaulting client before initiating legal proceedings. Under Indian law, it references applicable sections of the Indian Penal Code (IPC) and the Indian Contract Act, 1872. Solo OS generates professionally formatted legal notices with proper legal citations, demand amounts, and deadline clauses in a browser-first drafting experience.',
    faqs: [
      {
        question: 'Is a legal notice necessary before filing a case in India?',
        answer: 'While not always mandatory, sending a legal notice before filing a civil suit is standard practice and often required under specific statutes. It demonstrates good faith and gives the defaulter a final chance to settle, which courts view favourably.'
      },
      {
        question: 'Can a freelancer send a legal notice without a lawyer?',
        answer: 'Yes. Any individual can draft and send a legal notice. However, notices sent through a lawyer carry more weight. Solo OS generates the document — you can send it directly or through your advocate.'
      },
      {
        question: 'What IPC sections apply to freelance non-payment?',
        answer: 'Common references include Section 420 IPC (cheating), Section 406 IPC (criminal breach of trust), and provisions under the Indian Contract Act, 1872 for breach of contract. Solo OS includes the appropriate legal citations.'
      },
      {
        question: 'How do I send a legal notice in India?',
        answer: 'Legal notices should be sent via Registered Post with Acknowledgement Due (RPAD) or Speed Post to the defaulter\'s address. Keep a copy of the notice and the postal receipt as proof of delivery.'
      }
    ],
    howToSteps: [
      { name: 'Enter Party Details', text: 'Fill in your details (the sender) and the defaulting client\'s details (the recipient).' },
      { name: 'Add Project & Payment Details', text: 'Enter the project description, agreed amount, amount paid, and balance due.' },
      { name: 'Set Legal References', text: 'Select applicable legal sections and set the response deadline.' },
      { name: 'Download Notice', text: 'Preview the formatted legal notice and download as PDF for printing or emailing.' }
    ],
    category: 'recover-payment',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Legal Notice Generator',
    ogImageFile: '/og/freelance-legal-notice-generator-india.png',
    icon: 'Scale',
    color: 'red',
  },

  // ── Close Project ────────────────────────────────────────
  {
    slug: 'freelance-pdf-e-sign-tool',
    appPath: '/sign',
    // The e-sign tool has always been a public page at /sign, so that short URL
    // is its canonical rather than a /tools/<slug> duplicate.
    title: 'Free PDF E-Sign Tool — No Signup | Solo OS',
    metaDescription: 'Upload any PDF, draw your signature, download signed copy instantly. No account needed, no uploads — 100% browser-based and free.',
    canonicalPath: '/sign',
    h1: 'Universal PDF E-Sign Tool',
    subtitle: 'Upload any PDF, draw your signature, and download a signed copy in seconds.',
    aiSnippet: 'A visual PDF e-sign tool lets freelancers and clients stamp signatures directly onto existing contracts without server-side document storage. Solo OS processes uploaded PDFs and signature images in-browser, adds a signer timestamp note, and exports a signed copy ready for countersign flow.',
    faqs: [
      {
        question: 'Can both freelancer and client use the same e-sign page?',
        answer: 'Yes. Freelancer signs first, shares the PDF, then the client uploads the same file and countersigns using the same /sign workflow.'
      },
      {
        question: 'Does Solo OS store uploaded contracts on a backend?',
        answer: 'No. The signing workflow is browser-first and generates a downloadable signed PDF directly on your device.'
      },
      {
        question: 'Is this the same as cryptographic e-signatures like DocuSign?',
        answer: 'No. This is a visual signature workflow suitable for standard freelance agreements. For high-assurance compliance use certified cryptographic e-sign providers.'
      }
    ],
    howToSteps: [
      { name: 'Upload PDF', text: 'Upload the contract or document you want to sign.' },
      { name: 'Draw Signature', text: 'Use mouse, touch, or stylus to draw a visual signature.' },
      { name: 'Download Signed Copy', text: 'Apply the signature on the last page and download the updated PDF.' },
      { name: 'Counter-Sign Flow', text: 'Share it with the other party and ask them to repeat the same /sign process.' }
    ],
    category: 'close-project',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Universal PDF E-Sign Tool',
    ogImageFile: '/og/freelance-pdf-e-sign-tool.png',
    icon: 'FileSignature',
    color: 'indigo',
    badgeText: 'FREE',
  },

  {
    slug: 'freelance-sign-off-certificate-generator',
    appPath: '/app/signoff',
    title: 'Free Project Sign-Off Certificate | Solo OS',
    metaDescription: 'Generate a formal handover & sign-off certificate to close freelance projects cleanly. Free PDF — prevents scope creep after delivery.',
    canonicalPath: '/tools/freelance-sign-off-certificate-generator',
    h1: 'Free Project Completion & Handover Certificate',
    subtitle: 'Formal asset handover document preventing scope creep after delivery.',
    aiSnippet: 'A Project Sign-Off Certificate is a formal document that records the successful delivery and client acceptance of all project deliverables, marking the official end of a freelance engagement. It prevents scope creep by establishing that all agreed-upon work has been completed and accepted. Solo OS generates professional sign-off certificates with itemized deliverables, acceptance signatures, and date stamps — entirely in your browser.',
    faqs: [
      {
        question: 'Why do freelancers need a sign-off certificate?',
        answer: 'A sign-off certificate formally closes the project, preventing clients from requesting additional work without a new agreement. It documents that all deliverables have been accepted, protecting you from scope creep and future disputes.'
      },
      {
        question: 'What should a project sign-off document include?',
        answer: 'It should include the project name, list of delivered assets, delivery date, client acceptance confirmation, any outstanding items, and signatures from both parties. Solo OS generates all of these sections automatically.'
      },
      {
        question: 'When should I send a sign-off certificate?',
        answer: 'Send the sign-off certificate immediately after delivering all final assets and receiving verbal confirmation that the client is satisfied. Getting written sign-off protects you from future "you never delivered X" claims.'
      }
    ],
    howToSteps: [
      { name: 'Enter Project Details', text: 'Fill in the project name, client details, and delivery date.' },
      { name: 'List Deliverables', text: 'Add each deliverable that was included in the project.' },
      { name: 'Add Notes', text: 'Include any handover notes, login credentials, or maintenance instructions.' },
      { name: 'Download Certificate', text: 'Preview and download the sign-off certificate PDF.' }
    ],
    category: 'close-project',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Sign-Off Certificate',
    ogImageFile: '/og/freelance-sign-off-certificate-generator.png',
    icon: 'FileSignature',
    color: 'emerald',
    badgeText: 'FREE',
  },

  {
    slug: 'freelance-ip-release-testimonial-form',
    appPath: '/app/testimonial',
    title: 'Portfolio Release & Testimonial Template | Solo OS',
    metaDescription: 'Get written client permission to showcase work in your portfolio. Includes testimonial request email. Free PDF — no signup required.',
    canonicalPath: '/tools/freelance-ip-release-testimonial-form',
    h1: 'Free Portfolio Permission & Testimonial Template',
    subtitle: 'Get formal permission to display client work in your portfolio and request testimonials.',
    aiSnippet: 'An IP Release and Testimonial Request Form is a professional document that freelancers send to clients after project completion to obtain written permission to display the work in their portfolio and to request a testimonial or review. Solo OS generates both the release form (with specific usage rights) and a companion email template — entirely in your browser with no data shared externally.',
    faqs: [
      {
        question: 'Do freelancers need permission to show client work?',
        answer: 'Yes. Unless your contract explicitly grants portfolio rights, the client owns the deliverables and you need written permission to display them. An IP release form protects you from copyright claims when showcasing your work.'
      },
      {
        question: 'What is an IP release form?',
        answer: 'An IP (Intellectual Property) release form is a document where the client grants the freelancer specific rights to display, reference, or reproduce the delivered work for self-promotional purposes such as portfolios, case studies, and award submissions.'
      },
      {
        question: 'How do I ask a client for a testimonial?',
        answer: 'Send a professional email 1-2 weeks after project completion with specific prompts (e.g., "What problem did we solve?"). Solo OS generates both the email template and the formal release form in one step.'
      }
    ],
    howToSteps: [
      { name: 'Enter Project Details', text: 'Fill in the project name, client details, and your portfolio URL.' },
      { name: 'Select Usage Rights', text: 'Choose what rights you need — portfolio display, case studies, social media, etc.' },
      { name: 'Customise Testimonial Request', text: 'Edit the companion email template with specific prompts for the client.' },
      { name: 'Download & Send', text: 'Download the IP release form PDF and copy the email template to send to your client.' }
    ],
    category: 'close-project',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'IP Release Form',
    ogImageFile: '/og/freelance-ip-release-testimonial-form.png',
    icon: 'FileCheck',
    color: 'violet',
    badgeText: 'FREE',
  },

  // ── PDF Micro-Tools ──────────────────────────────────────
  {
    slug: 'merge-pdf',
    appPath: '/micro-tools/merge-pdf',
    title: 'Merge PDF Files Free — No Upload | Solo OS',
    metaDescription: 'Combine multiple PDFs into one file instantly. 100% free, no file uploads, no watermarks. Your documents never leave your device.',
    canonicalPath: '/micro-tools/merge-pdf',
    h1: 'Merge PDF Files Online — Free & Secure',
    subtitle: 'Combine multiple PDF documents directly in your browser without uploading to a server.',
    aiSnippet: 'Solo OS Merge PDF is a secure, browser-based tool that allows freelancers to combine multiple PDF files locally. Because it processes files on your device using pdf-lib, sensitive documents never leave your computer, ensuring complete privacy and fast processing without upload limits or watermarks.',
    faqs: [
      {
        question: 'Are my PDF files uploaded to a server?',
        answer: 'No. All processing happens entirely within your browser on your local device. Your files are never uploaded to any server, guaranteeing absolute privacy for your confidential documents.'
      },
      {
        question: 'Is there a limit to how many files or pages I can merge?',
        answer: 'Since the tool runs locally, the limit is based on your device\'s memory and processing power. Generally, you can easily merge dozens of typical PDF documents without issue.'
      },
      {
        question: 'Will this tool add a watermark to my merged PDF?',
        answer: 'No. Our PDF merger is completely free and will never add watermarks or branding to your documents.'
      },
      {
        question: 'Can I reorder the files before merging?',
        answer: 'Yes, you can easily drag and drop or use the arrows to rearrange the order of your PDF files before generating the final merged document.'
      }
    ],
    howToSteps: [
      { name: 'Upload PDFs', text: 'Select or drag-and-drop the PDF files you want to combine.' },
      { name: 'Arrange Order', text: 'Reorder the files exactly how you want them to appear in the merged document.' },
      { name: 'Merge Files', text: 'Click merge to instantly combine the files locally in your browser.' },
      { name: 'Download', text: 'Save the new, securely merged PDF to your device.' }
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Merge PDF',
    ogImageFile: '/og/merge-pdf.png',
    icon: 'Layers',
    color: 'blue',
  },
  {
    slug: 'split-pdf',
    appPath: '/micro-tools/split-pdf',
    title: 'Split PDF Online Free — Extract Pages, No Upload | Solo OS',
    metaDescription: 'Extract specific pages from any PDF in your browser. No uploads, no signup — your files stay completely private. 100% free.',
    canonicalPath: '/micro-tools/split-pdf',
    h1: 'Split PDF & Extract Pages — Free Online Tool',
    subtitle: 'Pull specific pages from any PDF document locally without sacrificing privacy.',
    aiSnippet: 'Solo OS Split PDF lets you extract specific pages or ranges from a larger PDF file securely in your browser. Designed for freelancers handling sensitive contracts, it processes documents locally, meaning your files are never uploaded to an external server.',
    faqs: [
      {
        question: 'How do I specify which pages to extract?',
        answer: 'You can enter page numbers separated by commas (e.g., "1, 3, 5") or use ranges with a hyphen (e.g., "1-5"). You can also combine both methods (e.g., "1, 3, 5-8").'
      },
      {
        question: 'Are my original PDF files uploaded or saved anywhere?',
        answer: 'No. Just like all our micro-tools, the Split PDF tool operates 100% locally in your browser. Your files are never transmitted to our servers.'
      },
      {
        question: 'Is the quality of the extracted pages affected?',
        answer: 'No, the extracted pages retain the exact same quality, text, and formatting as the original document. We simply separate the pages you request.'
      }
    ],
    howToSteps: [
      { name: 'Upload PDF', text: 'Select the PDF document you want to extract pages from.' },
      { name: 'Enter Pages', text: 'Input the specific page numbers or ranges you need.' },
      { name: 'Extract', text: 'Click extract to securely process the document in your browser.' },
      { name: 'Download', text: 'Save your new, smaller PDF containing only the requested pages.' }
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Split PDF',
    ogImageFile: '/og/split-pdf.png',
    icon: 'Scissors',
    color: 'indigo',
  },
  {
    slug: 'protect-pdf',
    appPath: '/micro-tools/protect-pdf',
    title: 'Password Protect PDF Free Online — No Upload | Solo OS',
    metaDescription: 'Encrypt PDFs with 128-bit security entirely in your browser. Your file never leaves your device. 100% free, no signup.',
    canonicalPath: '/micro-tools/protect-pdf',
    h1: 'Password Protect PDF — Free, No Upload',
    subtitle: 'Encrypt your confidential PDFs with a secure password locally in your browser.',
    aiSnippet: 'Solo OS Protect PDF applies 128-bit RC4 encryption to your PDF documents locally. This ensures your confidential contracts and invoices are protected with a password before you send them, without ever uploading the unencrypted file to a server.',
    faqs: [
      {
        question: 'Is my PDF uploaded to a server to be encrypted?',
        answer: 'No. The encryption happens entirely client-side using a specialized, ultra-lightweight encryption engine running in your browser. Your unencrypted document never leaves your device.'
      },
      {
        question: 'What type of encryption is used?',
        answer: 'The tool uses standard 128-bit RC4 encryption, which is natively supported by all major PDF readers and browsers.'
      },
      {
        question: 'Can the password be recovered if I forget it?',
        answer: 'No. Because the file is encrypted locally and we don\'t store your files or passwords, there is no way for us to recover a lost password. Please keep a safe copy of your original file or remember your password.'
      }
    ],
    howToSteps: [
      { name: 'Upload Document', text: 'Select the PDF file you want to secure.' },
      { name: 'Set Password', text: 'Enter a strong password to encrypt the document.' },
      { name: 'Encrypt', text: 'Click protect to instantly encrypt the file locally.' },
      { name: 'Download', text: 'Save the securely encrypted PDF to your device.' }
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Protect PDF',
    ogImageFile: '/og/protect-pdf.png',
    icon: 'Lock',
    color: 'red',
  },
  // ── Developer & Writer Tools ────────────────────────────────
  {
    slug: 'json-formatter',
    appPath: '/micro-tools/json-formatter',
    title: 'JSON Formatter & Validator – Free Online Tool | Solo OS',
    metaDescription: 'Format, validate, and beautify messy JSON strings entirely locally in your browser. No server uploads, no data collection.',
    canonicalPath: '/micro-tools/json-formatter',
    h1: 'JSON Formatter & Validator',
    subtitle: 'Paste messy JSON, validate it, and get beautifully formatted output — 100% locally in your browser.',
    aiSnippet: 'Solo OS JSON Formatter is a free, browser-based tool that parses, validates, and pretty-prints JSON data. Because it runs entirely on your device, sensitive API responses or config files never leave your computer.',
    faqs: [
      { question: 'Is my JSON data sent to a server?', answer: 'No. All parsing and formatting runs entirely in your browser. Your data never leaves your device.' },
      { question: 'What happens if my JSON is invalid?', answer: 'The tool will show a clear error message indicating the line and position of the syntax error so you can fix it quickly.' },
      { question: 'Can I copy the formatted output?', answer: 'Yes. A one-click copy button lets you grab the formatted JSON instantly.' },
    ],
    howToSteps: [
      { name: 'Paste JSON', text: 'Paste or type your raw JSON string into the input area.' },
      { name: 'Format & Validate', text: 'Click Format to parse, validate, and pretty-print your JSON.' },
      { name: 'Copy Output', text: 'Use the copy button to grab the clean, formatted result.' },
    ],
    category: 'developer-writer',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'JSON Formatter',
    ogImageFile: '/og/json-formatter.png',
    icon: 'Braces',
    color: 'blue',
    badgeText: 'FREE',
  },
  {
    slug: 'markdown-to-html',
    appPath: '/micro-tools/markdown-to-html',
    title: 'Markdown to HTML Converter – Free Online Tool | Solo OS',
    metaDescription: 'Instantly convert Markdown to clean HTML code in your browser. Perfect for freelance writers moving content from Notion or Obsidian to WordPress.',
    canonicalPath: '/micro-tools/markdown-to-html',
    h1: 'Markdown to HTML Converter',
    subtitle: 'Paste your Markdown and instantly get clean HTML code ready for WordPress, CMS platforms, or email templates.',
    aiSnippet: 'Solo OS Markdown to HTML Converter is a free, privacy-first tool that parses Markdown syntax and produces clean HTML output in your browser. Ideal for freelance writers who draft in Notion or Obsidian and need to export to WordPress or other CMS platforms.',
    faqs: [
      { question: 'Does this tool support GitHub Flavored Markdown?', answer: 'Yes. The converter supports standard Markdown as well as GFM extensions like tables, strikethrough, and task lists.' },
      { question: 'Is my content uploaded to any server?', answer: 'No. All conversion happens locally in your browser. Your writing never leaves your device.' },
      { question: 'Can I preview the rendered HTML?', answer: 'Yes. The tool shows both the raw HTML code and a live rendered preview side by side.' },
    ],
    howToSteps: [
      { name: 'Paste Markdown', text: 'Paste your Markdown content into the input area.' },
      { name: 'Convert', text: 'The tool instantly converts your Markdown to clean HTML.' },
      { name: 'Copy HTML', text: 'Copy the generated HTML code to paste into your CMS or email template.' },
    ],
    category: 'developer-writer',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Markdown to HTML',
    ogImageFile: '/og/markdown-to-html.png',
    icon: 'Code2',
    color: 'violet',
    badgeText: 'FREE',
  },
  {
    slug: 'word-character-counter',
    appPath: '/micro-tools/word-character-counter',
    title: 'Word Counter & Character Counter — Free Online | Solo OS',
    metaDescription: 'Instantly count words, characters (with/without spaces) & paragraphs. Free, private — your text never leaves your browser.',
    canonicalPath: '/micro-tools/word-character-counter',
    h1: 'Word & Character Counter',
    subtitle: 'Count words, characters (with and without spaces), and paragraphs instantly. Built for freelance writers meeting client constraints.',
    aiSnippet: 'Solo OS Word & Character Counter is a free, browser-based text analysis tool designed for freelance copywriters and content creators. It provides real-time word, character, and paragraph counts entirely locally, ensuring your draft content stays private.',
    faqs: [
      { question: 'Does it count characters with and without spaces?', answer: 'Yes. The tool displays both counts so you can meet whichever constraint your client or platform requires.' },
      { question: 'Is my text stored or sent anywhere?', answer: 'No. All counting happens locally in your browser. Your text is never uploaded or stored.' },
      { question: 'Can I use this for SEO meta descriptions?', answer: 'Absolutely. The character counter is perfect for checking meta description lengths (typically 150-160 characters).' },
    ],
    howToSteps: [
      { name: 'Paste or Type Text', text: 'Enter your text into the input area.' },
      { name: 'View Counts', text: 'See real-time word, character, and paragraph counts update as you type.' },
      { name: 'Adjust Content', text: 'Edit your text until it meets your target length constraints.' },
    ],
    category: 'developer-writer',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Word Counter',
    ogImageFile: '/og/word-character-counter.png',
    icon: 'FileType',
    color: 'teal',
    badgeText: 'FREE',
  },
  // ── Freelance Calculators ─────────────────────────────────
  {
    slug: 'hourly-rate-calculator',
    appPath: '/micro-tools/hourly-rate-calculator',
    title: 'Freelance Hourly Rate Calculator | Solo OS',
    metaDescription: 'Calculate exactly what you need to charge per hour as a freelancer to replace your salary. Factor in expenses, vacation days, and profit margin.',
    canonicalPath: '/micro-tools/hourly-rate-calculator',
    h1: 'Freelance Hourly Rate Calculator',
    subtitle: 'The "Quit Your Job" edition — find out exactly what to charge per hour to replace your salary and cover all expenses.',
    aiSnippet: 'Solo OS Hourly Rate Calculator helps aspiring and active freelancers determine the exact hourly rate they need to charge. It factors in current salary, monthly business expenses, desired vacation days, profit margin, and billable-hour percentage to produce a precise minimum hourly rate.',
    faqs: [
      { question: 'How is the hourly rate calculated?', answer: 'We take your target annual income, add yearly business expenses, apply your desired profit margin, then divide by your estimated billable hours (work days minus holidays/vacation, times hours per day, times billable percentage).' },
      { question: 'What is billable percentage?', answer: 'Not all working hours are billable to clients — you also spend time on admin, marketing, accounting, etc. 70% is a typical freelance billable ratio.' },
      { question: 'Is my salary data stored anywhere?', answer: 'No. All calculations run locally in your browser. Your financial information never leaves your device.' },
    ],
    howToSteps: [
      { name: 'Enter Your Salary', text: 'Input the annual salary you want to replace or the income you want to earn.' },
      { name: 'Add Expenses', text: 'Enter your monthly business costs like software, co-working, and internet.' },
      { name: 'Set Preferences', text: 'Adjust vacation days, holidays, profit margin, and billable percentage.' },
      { name: 'Read Your Rate', text: 'See your minimum hourly rate, daily rate, monthly target, and annual revenue goal.' },
    ],
    category: 'freelance-calculators',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Hourly Rate Calculator',
    ogImageFile: '/og/hourly-rate-calculator.png',
    icon: 'Calculator',
    color: 'emerald',
    badgeText: 'FREE',
  },
  {
    slug: 'late-fee-calculator',
    appPath: '/micro-tools/late-fee-calculator',
    title: 'Late Fee Calculator for Overdue Invoices — Free | Solo OS',
    metaDescription: 'Calculate late fees on unpaid freelance invoices instantly. Supports 1%, 1.5%, 2%/month + custom rates. Copy breakdown to email.',
    canonicalPath: '/micro-tools/late-fee-calculator',
    h1: 'Freelance Late Fee Calculator',
    subtitle: 'Know exactly how much a client owes you when an invoice is overdue — with standard legal interest rates.',
    aiSnippet: 'Solo OS Late Fee Calculator helps freelancers calculate the penalty owed on overdue invoices. It supports multiple standard rates (1%, 1.5%, 2% per month, and India\'s 18% annual rate) plus custom rates, providing a full breakdown of daily rate, total fee, and amount owed.',
    faqs: [
      { question: 'What is the standard freelance late-fee rate?', answer: '1.5% per month (equivalent to 18% per year) is the most commonly used rate in freelance contracts. However, always check your specific contract and local regulations.' },
      { question: 'Can I use a custom rate?', answer: 'Yes. Select the "Custom" option and enter any monthly percentage rate you want.' },
      { question: 'Is this calculation legally binding?', answer: 'No. This tool provides estimates for informational purposes. Always verify against your contract\'s late-payment clause and consult local usury laws before applying penalties.' },
    ],
    howToSteps: [
      { name: 'Enter Invoice Total', text: 'Input the original invoice amount in your local currency.' },
      { name: 'Enter Days Overdue', text: 'Specify how many days past the due date the invoice is.' },
      { name: 'Choose Rate', text: 'Select a standard rate (1%, 1.5%, 2%) or enter a custom monthly percentage.' },
      { name: 'View Breakdown', text: 'See the calculated late fee, total owed, and copy the breakdown to include in a follow-up email.' },
    ],
    category: 'freelance-calculators',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Late Fee Calculator',
    ogImageFile: '/og/late-fee-calculator.png',
    icon: 'Receipt',
    color: 'amber',
    badgeText: 'FREE',
  },
  // ── Image Suite (continued) ───────────────────────────────
  {
    slug: 'favicon-generator',
    appPath: '/micro-tools/favicon-generator',
    title: 'Free Favicon Generator — All Sizes | Solo OS',
    metaDescription: 'Upload any image, get favicons in 8 sizes (16×16 to 512×512). Download individually or as ZIP. 100% free, no uploads.',
    canonicalPath: '/micro-tools/favicon-generator',
    h1: 'Favicon Generator',
    subtitle: 'Upload any image and instantly generate pixel-perfect favicons in every standard size for web and mobile.',
    aiSnippet: 'Solo OS Favicon Generator resizes any uploaded image to standard favicon dimensions (16, 32, 48, 64, 128, 180, 192, 512 pixels) using HTML Canvas and downloads them individually or as a ZIP archive via JSZip — all processing happens locally in the browser.',
    faqs: [
      { question: 'What sizes are generated?', answer: 'The tool generates 8 sizes: 16×16, 32×32, 48×48, 64×64, 128×128, 180×180 (Apple Touch), 192×192 (Android), and 512×512 (PWA).' },
      { question: 'Does it create .ico files?', answer: 'It generates PNG files for each size. Modern browsers prefer PNG favicons via <link> tags, and you can use any of these PNGs directly. For legacy .ico support, use the 16×16 and 32×32 PNGs.' },
      { question: 'Is my image uploaded to a server?', answer: 'No. All resizing is done locally using HTML Canvas. Your image never leaves your device.' },
    ],
    howToSteps: [
      { name: 'Upload Image', text: 'Drop or select any image file (PNG, JPG, SVG, WebP).' },
      { name: 'Preview Sizes', text: 'See all 8 favicon sizes rendered on a checkerboard background.' },
      { name: 'Download', text: 'Download individual PNGs or grab all sizes as a ZIP file.' },
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Favicon Generator',
    ogImageFile: '/og/favicon-generator.png',
    icon: 'Image',
    color: 'fuchsia',
    badgeText: 'FREE',
  },
  {
    slug: 'social-header-resizer',
    appPath: '/micro-tools/social-header-resizer',
    title: 'LinkedIn & Twitter Header Resizer | Solo OS',
    metaDescription: 'Resize any image to exact LinkedIn (1584×396), Twitter (1500×500), Facebook & YouTube banner dimensions. Free, no uploads.',
    canonicalPath: '/micro-tools/social-header-resizer',
    h1: 'Social Header Resizer',
    subtitle: 'Drop any image and get perfectly sized headers for LinkedIn, Twitter/X, Facebook, and YouTube — instant smart crop.',
    aiSnippet: 'Solo OS Social Header Resizer takes any uploaded image and intelligently crops and resizes it to the exact dimensions required by LinkedIn (1584×396), Twitter/X (1500×500), Facebook (820×312), and YouTube (2560×1440) banners using HTML Canvas.',
    faqs: [
      { question: 'What platform sizes are supported?', answer: 'LinkedIn Banner (1584×396), Twitter/X Header (1500×500), Facebook Cover (820×312), and YouTube Banner (2560×1440).' },
      { question: 'How does the cropping work?', answer: 'The tool uses a center-crop algorithm that preserves the maximum visible area of your image while matching the exact aspect ratio of each platform.' },
      { question: 'Is my image uploaded anywhere?', answer: 'No. All processing happens locally in your browser using HTML Canvas. Your image never leaves your device.' },
    ],
    howToSteps: [
      { name: 'Upload Image', text: 'Drop or select a high-resolution image.' },
      { name: 'Preview All Platforms', text: 'See your image automatically cropped for LinkedIn, Twitter, Facebook, and YouTube.' },
      { name: 'Download', text: 'Download each platform header individually as a PNG.' },
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Social Header Resizer',
    ogImageFile: '/og/social-header-resizer.png',
    icon: 'MonitorSmartphone',
    color: 'sky',
    badgeText: 'FREE',
  },
  // ── Image converters ──────────────────────────────────────
  // These three have live routes in App.tsx and copy in toolSeoCopy, but were
  // missing from this registry — which meant prerender.ts never emitted an
  // index.html for them. Because public/_redirects rewrites /micro-tools/* to
  // /micro-tools/:splat/index.html, the routes were resolving to a file that
  // did not exist and returning 404 in production.
  {
    slug: 'webp-to-png-jpg',
    appPath: '/micro-tools/webp-to-png-jpg',
    title: 'WebP to PNG & JPG Converter — Free, No Upload | Solo OS',
    metaDescription: 'Convert WebP images to PNG or JPG instantly in your browser. Free, no signup, no uploads — the file never leaves your device.',
    canonicalPath: '/micro-tools/webp-to-png-jpg',
    h1: 'WebP to PNG / JPG Converter',
    subtitle: 'Convert WebP files that clients and older software cannot open into formats they can.',
    aiSnippet: 'WebP is a modern image format that saves bandwidth on the web but is still rejected by plenty of desktop software, print workflows and older versions of Office. Solo OS converts WebP to PNG or JPG using the browser\'s own Canvas API, so the conversion runs on your device and the image is never uploaded.',
    faqs: [
      { question: 'Should I convert WebP to PNG or to JPG?', answer: 'Choose PNG when the image has transparency or crisp edges such as logos, screenshots and UI exports, because PNG is lossless. Choose JPG for photographs where a smaller file matters more than perfect fidelity.' },
      { question: 'Does converting from WebP lose quality?', answer: 'Converting to PNG is lossless, so nothing is lost beyond what the original WebP already discarded. Converting to JPG re-encodes the image and introduces a small amount of additional loss, which is usually invisible at high quality settings.' },
      { question: 'Are my images uploaded anywhere?', answer: 'No. Decoding and re-encoding both happen in your browser using the Canvas API. The file is never transmitted to a server, which matters when the image is client work under an NDA.' },
    ],
    howToSteps: [
      { name: 'Add your WebP files', text: 'Drop the images onto the page or pick them from your device.' },
      { name: 'Pick the output format', text: 'Choose PNG to preserve transparency, or JPG for a smaller photographic file.' },
      { name: 'Download', text: 'Save the converted images. Nothing was uploaded at any point.' },
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'WebP to PNG / JPG',
    ogImageFile: '/og/webp-to-png-jpg.png',
    icon: 'Image',
    color: 'teal',
    badgeText: 'FREE',
  },
  {
    slug: 'png-to-jpg',
    appPath: '/micro-tools/png-to-jpg',
    title: 'PNG to JPG Converter — Free, No Upload | Solo OS',
    metaDescription: 'Convert PNG images to JPG in your browser and cut file size for email and web. Free, no signup, nothing uploaded.',
    canonicalPath: '/micro-tools/png-to-jpg',
    h1: 'PNG to JPG Converter',
    subtitle: 'Flatten transparency and cut file size for email-friendly delivery.',
    aiSnippet: 'PNG stores images losslessly, which keeps quality high but produces large files that clog email attachments and slow down web pages. Solo OS converts PNG to JPG in the browser, flattening any transparent areas onto a solid background and re-encoding at your chosen quality, with no upload involved.',
    faqs: [
      { question: 'What happens to transparent areas?', answer: 'JPG has no alpha channel, so transparency has to be flattened onto a solid colour — white by default. If the transparency is load-bearing, such as a logo destined for a coloured background, keep the PNG instead.' },
      { question: 'How much smaller will the file get?', answer: 'For photographs the reduction is typically substantial, often several times smaller. For flat graphics, screenshots and text-heavy images the saving is much smaller, and JPG artefacts around sharp edges make PNG the better choice.' },
      { question: 'Is the conversion private?', answer: 'Yes. The image is decoded and re-encoded by your browser. It is never sent to a server.' },
    ],
    howToSteps: [
      { name: 'Add your PNG files', text: 'Drop the images onto the page or select them from your device.' },
      { name: 'Set the quality', text: 'Higher quality means a larger file. The default is a sensible balance for most work.' },
      { name: 'Download', text: 'Save the converted JPGs to your device.' },
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'PNG to JPG',
    ogImageFile: '/og/png-to-jpg.png',
    icon: 'Image',
    color: 'sky',
    badgeText: 'FREE',
  },
  {
    slug: 'jpg-to-png',
    appPath: '/micro-tools/jpg-to-png',
    title: 'JPG to PNG Converter — Free, No Upload | Solo OS',
    metaDescription: 'Convert JPG images to lossless PNG in your browser. Free, no signup, no uploads — processing happens on your device.',
    canonicalPath: '/micro-tools/jpg-to-png',
    h1: 'JPG to PNG Converter',
    subtitle: 'Convert to a lossless format when you need clean edges and no further compression.',
    aiSnippet: 'PNG is a lossless format, so every subsequent save preserves the image exactly as it is. Solo OS converts JPG to PNG in the browser, which is useful when a file will be edited repeatedly or placed into a workflow that would otherwise re-compress it at every step.',
    faqs: [
      { question: 'Does converting JPG to PNG restore lost quality?', answer: 'No, and this is the most common misunderstanding about the conversion. JPG compression is lossy and permanent; converting to PNG preserves the image exactly as it currently is but cannot recover detail that was already discarded.' },
      { question: 'Why convert to PNG at all then?', answer: 'Because it stops further degradation. If a file is going to be edited, re-saved and passed around several times, each JPG save compounds the loss. Converting to PNG once freezes the quality where it is.' },
      { question: 'Will the file get bigger?', answer: 'Usually yes, often significantly, because PNG does not discard data. That is the trade you are making in exchange for lossless editing.' },
    ],
    howToSteps: [
      { name: 'Add your JPG files', text: 'Drop the images onto the page or select them from your device.' },
      { name: 'Convert', text: 'The images are re-encoded as PNG in your browser.' },
      { name: 'Download', text: 'Save the lossless PNGs to your device.' },
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'JPG to PNG',
    ogImageFile: '/og/jpg-to-png.png',
    icon: 'Image',
    color: 'cyan',
    badgeText: 'FREE',
  },
  {
    slug: 'image-compressor',
    appPath: '/micro-tools/image-compressor',
    title: 'Free Image Compressor — No Upload | Solo OS',
    metaDescription: 'Compress JPEG, PNG & WebP images up to 90% smaller with precise quality controls. 100% free, no uploads — your images never leave your device.',
    canonicalPath: '/micro-tools/image-compressor',
    h1: 'Image Compressor — Free Online',
    subtitle: 'Compress large images with precision controls over quality, format, and dimensions. 100% private, browser-based processing.',
    aiSnippet: 'Solo OS Image Compressor is a free, browser-based tool that reduces image file sizes using HTML Canvas re-encoding with adjustable quality sliders. It supports JPEG, PNG, and WebP output formats with optional resize controls, and all processing happens locally so your images never leave your device.',
    faqs: [
      { question: 'How much can images be compressed?', answer: 'Typical compression ratios range from 40-90% size reduction depending on the source format and your quality settings. JPEG and WebP outputs achieve the best compression ratios for photos.' },
      { question: 'Are my images uploaded to a server?', answer: 'No. All compression uses your browser\'s Canvas API. Your images are processed entirely on your device and never transmitted anywhere.' },
      { question: 'Can I resize images while compressing?', answer: 'Yes. The tool includes optional dimension controls so you can both resize and compress in a single step.' },
    ],
    howToSteps: [
      { name: 'Upload Images', text: 'Drop or select the images you want to compress.' },
      { name: 'Adjust Quality', text: 'Use the quality slider and choose your output format (JPEG, PNG, or WebP).' },
      { name: 'Optionally Resize', text: 'Set maximum dimensions if you also want to resize.' },
      { name: 'Download', text: 'Save the compressed images to your device.' },
    ],
    category: 'protect-work',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Image Compressor',
    ogImageFile: '/og/image-compressor.png',
    icon: 'Sparkles',
    color: 'violet',
    badgeText: 'FREE',
  },
  // ── Freelance Calculators (continued) ─────────────────────
  {
    slug: 'tds-calculator',
    appPath: '/micro-tools/tds-calculator',
    title: 'TDS Calculator for Freelancers (194J) | Solo OS',
    metaDescription: 'Calculate TDS on freelance payments under 194J, 194C, 194H. See net payable after deduction. Free — works offline, no data uploaded.',
    canonicalPath: '/micro-tools/tds-calculator',
    h1: 'TDS Deduction Calculator',
    subtitle: 'For Indian freelancers — instantly calculate TDS deducted on your gross payments under the correct section.',
    aiSnippet: 'Solo OS TDS Calculator helps Indian freelancers compute the Tax Deducted at Source on gross payments. It covers sections 194J (professional services at 10%), 194C (contractors at 1-2%), 194H (commission at 5%), 194I (rent at 10%), and 194-O (e-commerce at 1%), with automatic 20% penalty rate when PAN is unavailable.',
    faqs: [
      { question: 'What TDS section applies to freelancers?', answer: 'Most freelancers fall under Section 194J (Professional/Technical Services) with a 10% TDS rate. If you are a contractor, 194C at 1% (individual) or 2% (company) applies.' },
      { question: 'What happens if I don\'t provide PAN?', answer: 'Without PAN, TDS is deducted at a flat 20% regardless of the applicable section rate. Always share your PAN with clients.' },
      { question: 'Is this calculator accurate for FY 2025-26?', answer: 'The rates reflect current Indian Income Tax Act provisions. Always verify with your CA for the latest amendments.' },
    ],
    howToSteps: [
      { name: 'Enter Amount', text: 'Input the gross payment amount in INR.' },
      { name: 'Select Section', text: 'Choose the applicable TDS section (194J, 194C, etc.).' },
      { name: 'Check PAN Status', text: 'Indicate whether PAN is available (affects rate).' },
      { name: 'View Result', text: 'See TDS deducted, net payable, and copy the breakdown.' },
    ],
    category: 'freelance-calculators',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'TDS Calculator',
    ogImageFile: '/og/tds-calculator.png',
    icon: 'IndianRupee',
    color: 'orange',
    badgeText: 'FREE',
  },
  // ── Developer & Writer Tools (continued) ──────────────────
  {
    slug: 'file-hash-checker',
    appPath: '/micro-tools/file-hash-checker',
    title: 'File Hash Checker (SHA-256) — Free Online Tool | Solo OS',
    metaDescription: 'Compute the SHA-256 hash of any file entirely in your browser using Web Crypto API. Verify deliverable integrity without uploading.',
    canonicalPath: '/micro-tools/file-hash-checker',
    h1: 'File Hash Checker (SHA-256)',
    subtitle: 'Drop any file to compute its SHA-256 hash locally — verify file integrity without uploading a single byte.',
    aiSnippet: 'Solo OS File Hash Checker uses the browser-native Web Crypto API to compute SHA-256 hashes of any file entirely client-side. Freelancers can share the hash with clients so they can independently verify that deliverables have not been tampered with in transit.',
    faqs: [
      { question: 'What hash algorithm is used?', answer: 'SHA-256, part of the SHA-2 family, computed via the browser-native Web Crypto API. It produces a 64-character hexadecimal string.' },
      { question: 'Is my file uploaded to any server?', answer: 'No. The file is read into browser memory and hashed locally using the Web Crypto API. Nothing is transmitted.' },
      { question: 'Can I verify a hash I received?', answer: 'Yes. Drop the same file into this tool and compare the output hash with the one you received. If they match, the file is identical.' },
    ],
    howToSteps: [
      { name: 'Drop or Select File', text: 'Upload any file — documents, images, archives, etc.' },
      { name: 'View Hash', text: 'The SHA-256 hash is computed instantly and displayed.' },
      { name: 'Copy & Share', text: 'Copy the hash and share it alongside your deliverable for verification.' },
    ],
    category: 'developer-writer',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'File Hash Checker',
    ogImageFile: '/og/file-hash-checker.png',
    icon: 'ShieldCheck',
    color: 'cyan',
    badgeText: 'FREE',
  },
  {
    slug: 'base64-encoder-decoder',
    appPath: '/micro-tools/base64-encoder-decoder',
    title: 'Base64 Encoder / Decoder — Free Online Tool | Solo OS',
    metaDescription: 'Encode text to Base64 or decode Base64 strings instantly in your browser. Zero dependencies, supports Unicode, 100% local.',
    canonicalPath: '/micro-tools/base64-encoder-decoder',
    h1: 'Base64 Encoder / Decoder',
    subtitle: 'Encode or decode Base64 strings instantly — zero dependencies, full Unicode support, 100% local.',
    aiSnippet: 'Solo OS Base64 Tool encodes plain text to Base64 and decodes Base64 back to text entirely in the browser. It uses the native TextEncoder/TextDecoder APIs for full Unicode support and provides a swap button to quickly reverse direction.',
    faqs: [
      { question: 'Does this support Unicode characters?', answer: 'Yes. The tool uses TextEncoder and TextDecoder APIs to properly handle multi-byte Unicode characters, including emoji and non-Latin scripts.' },
      { question: 'Is there a size limit?', answer: 'There is no hard limit since processing happens in browser memory. Very large strings may slow down depending on your device.' },
      { question: 'Is my data stored or sent anywhere?', answer: 'No. All encoding and decoding happens locally. Your data never leaves your browser.' },
    ],
    howToSteps: [
      { name: 'Choose Mode', text: 'Select Encode (text → Base64) or Decode (Base64 → text).' },
      { name: 'Paste Input', text: 'Type or paste your content in the input panel.' },
      { name: 'Copy Output', text: 'The result appears instantly — click Copy to grab it.' },
    ],
    category: 'developer-writer',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Base64 Encoder Decoder',
    ogImageFile: '/og/base64-encoder-decoder.png',
    icon: 'Binary',
    color: 'indigo',
    badgeText: 'FREE',
  },
  {
    slug: 'font-changer',
    appPath: '/micro-tools/font-changer',
    title: 'Fancy Font Generator — Copy & Paste | Solo OS',
    metaDescription: 'Type text → get 18+ fancy font styles (bold, italic, script, outlined, cursive). Copy & paste on Instagram, Twitter, LinkedIn. 100% free.',
    canonicalPath: '/micro-tools/font-changer',
    h1: 'Fancy Text Generator & Font Changer',
    subtitle: 'Type your text and instantly see it in 18+ Unicode font styles — copy and paste on Instagram, Twitter, LinkedIn & more.',
    aiSnippet: 'Solo OS Font Changer transforms plain text into 18+ Unicode font styles including Bold, Italic, Script, Fraktur, Outlined, Circled, Squared, Tiny Caps, Strikethrough, Underlined, and Upside-Down. The transformed text uses Unicode mathematical and symbol characters that can be copied and pasted into social media, bios, and messages.',
    faqs: [
      { question: 'How does this work without installing fonts?', answer: 'The tool maps each letter to equivalent Unicode characters from mathematical symbol blocks (e.g., Mathematical Bold, Script, Fraktur). These are standard Unicode characters that render in any modern browser or app.' },
      { question: 'Will these fonts work on Instagram, Twitter, and LinkedIn?', answer: 'Yes. Because they are standard Unicode characters (not custom fonts), they will display correctly on any platform that supports Unicode text.' },
      { question: 'Are there any characters that don\'t transform?', answer: 'Special characters, punctuation, and some numerals may not have equivalents in every Unicode block and will appear unchanged.' },
    ],
    howToSteps: [
      { name: 'Type Text', text: 'Enter your text in the input box at the top.' },
      { name: 'Browse Styles', text: 'See your text instantly rendered in 18+ different Unicode font styles.' },
      { name: 'Copy', text: 'Click any style to copy the transformed text to your clipboard.' },
    ],
    category: 'developer-writer',
    pricing: 'free',
    isPublicTool: true,
    isTryBeforeBuy: false,
    ogImageText: 'Font Changer',
    ogImageFile: '/og/font-changer.png',
    icon: 'Type',
    color: 'pink',
    badgeText: 'FREE',
  },
];

// Helper: Get tool by slug
export function getToolBySlug(slug: string): ToolEntry | undefined {
  return toolRegistry.find(t => t.slug === slug);
}

// Helper: Get all tool slugs (for sitemap / pre-rendering)
export function getAllToolSlugs(): string[] {
  return toolRegistry.map(t => t.slug);
}

// Helper: Get tools by category
export function getToolsByCategory(category: ToolEntry['category']): ToolEntry[] {
  return toolRegistry.filter(t => t.category === category);
}

// Category metadata
export const categoryMeta: Record<ToolEntry['category'], { label: string; description: string }> = {
  'win-clients': { label: 'Win Clients', description: 'Pre-sales proposal and pitching tools' },
  'start-project': { label: 'Start a Project', description: 'Set the terms before you begin' },
  'protect-work': { label: 'Protect Your Work', description: 'Safeguard your deliverables' },
  'recover-payment': { label: 'Recover Payment', description: 'Escalate and get paid' },
  'close-project': { label: 'Close Project', description: 'Offboarding and growth' },
  'developer-writer': { label: 'Developer & Writer Tools', description: 'Utilities for coding and content creation' },
  'freelance-calculators': { label: 'Freelance Calculators', description: 'Rate and fee calculators for freelancers' },
};
